import { ApiResponse } from 'apisauce'
import { call, put, select } from 'redux-saga/effects'
import moment from 'moment'
import produce from 'immer'

import { ApplicationState } from 'src/store'
import { ClientNotifyCatch } from 'src/config/bugsnag'
import { RequestTicket, SaveRequestStandardAssignments } from 'src/types'
import api from 'src/services/api'
import findBasicApiResponseErrors from 'src/services/findBasicApiResponseErrors'
import i18n from 'src/config/i18n'
import objectToQueryString from 'src/helpers/object-to-query-string'

import {
  ResponseDataFullRequest,
  ResponseDataRequestItineraries,
  ResponseDataRequests,
  ResponseDataRequestStandardAssignments,
  ResponseDataTickets
} from './types'
import * as RequestsActions from './actions'

import { resetAir } from '../air/actions'
import { resetHotels } from '../hotels/actions'
import { resetVehicles } from '../vehicles/actions'

const REQUEST_PAGE_SIZE = 10

export function* getFullRequest({
  payload: { origin, request_id }
}: ReturnType<typeof RequestsActions.getFullRequest>) {
  try {
    const queryString = !origin ? '' : `?env=${origin}`

    const {
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    const response: ApiResponse<ResponseDataFullRequest> = yield call(
      api.get,
      `/organizations/${organization.organization_id}/requests/${request_id}/full_request${queryString}`
    )

    if (response.ok) {
      yield put(RequestsActions.getFullRequestSuccess(response.data))
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.getFullRequestFailure(message))
  } catch (err) {
    ClientNotifyCatch('saga getFullRequest', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.getFullRequestFailure(message))
  }
}

export function* getMyRequests({
  payload: { options }
}: ReturnType<typeof RequestsActions.getMyRequests>) {
  try {
    const {
      requests,
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    const filterNumbers = requests.filterRequestNumber && {
      request_numbers: requests.filterRequestNumber
    }
    const queryString = objectToQueryString(
      Object.assign(
        {
          page_size: REQUEST_PAGE_SIZE,
          page: options.page || 1,
          ...(filterNumbers || {})
        },
        ...requests.filterStatus.map((status: string) => ({
          [status]: 'TRUE'
        }))
      )
    )

    const response: ApiResponse<ResponseDataRequests> = yield call(
      api.get,
      `organizations/${organization.organization_id}/requests${queryString}`
    )

    if (response.ok) {
      // check if request_id is truth
      const filteredRequest = {
        ...response.data,
        requests: response.data.requests.filter(({ request_id }) => request_id)
      }
      yield put(RequestsActions.getMyRequestsSuccess(filteredRequest))
      yield put(
        RequestsActions.setRequestTotals({
          request_totals: response.data.request_totals
        })
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.getMyRequestsFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga getMyRequests', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.getMyRequestsFailure(message))
  }
}

export function* saveFullRequest({
  payload: { request, setFullRequest }
}: ReturnType<typeof RequestsActions.saveFullRequest>) {
  try {
    const {
      me: { organization }
    } = yield select((state: ApplicationState) => state)

    let hasToodopayTickets = false
    const encodedRequest = produce(request, draft => {
      draft.justification = !draft.justification ? '' : draft.justification
      draft.contract_id = draft.contract_id ? +draft.contract_id : null
      draft.organization_id = organization.organization_id
      draft.request_id = draft.request_id ? +draft.request_id : null
      draft.request_number = draft.request_number ? +draft.request_number : null
      draft.request_itineraries.forEach(item => {
        item.started_on = moment.unix(item.started_on).unix()
        item.ended_on = moment.unix(item.ended_on).unix()
        item.reference_id = +item.reference_id
        item.request_id = draft.request_id
        item.request_itinerary_id = item.request_itinerary_id
          ? +item.request_itinerary_id
          : null
      })

      draft.request_standard_assignments.request_standard_billings.forEach(
        item => {
          item.billing_type_id = +item.billing_type_id
          item.request_id = draft.request_id
          item.request_standard_billing_id = item.request_standard_billing_id
            ? +item.request_standard_billing_id
            : null
          item.target_id = +item.target_id
        }
      )

      draft.request_standard_assignments.request_standard_sharings.forEach(
        item => {
          item.contract_id = +item.contract_id
          item.cost_center_id = +item.cost_center_id
          item.request_id = draft.request_id
          item.request_standard_sharing_id = item.request_standard_sharing_id
            ? +item.request_standard_sharing_id
            : null
        }
      )

      hasToodopayTickets = draft.request_tickets.some(ticket =>
        Boolean(!ticket.request_id && ticket.request_ticket_id)
      )
      draft.request_tickets.forEach(item => {
        item.request_ticket_id = item.request_ticket_id
          ? +item.request_ticket_id
          : null
        item.request_ticket_number = item.request_ticket_number
          ? +item.request_ticket_number
          : null

        item.request_ticket_title_advances.forEach(advanceItem => {
          advanceItem.currency_id = +advanceItem.currency_id
          advanceItem.financial_resource_id = +advanceItem.financial_resource_id
          advanceItem.request_ticket_title_advance_id =
            advanceItem.request_ticket_title_advance_id
              ? +advanceItem.request_ticket_title_advance_id
              : null
          advanceItem.request_ticket_title_advance_number =
            advanceItem.request_ticket_title_advance_number
              ? +advanceItem.request_ticket_title_advance_number
              : null
        })

        item.request_ticket_user_data.forEach(userDataItem => {
          userDataItem.request_ticket_user_data_id =
            userDataItem.request_ticket_user_data_id
              ? +userDataItem.request_ticket_user_data_id
              : null
          if (userDataItem.traveler) {
            userDataItem.traveler.user_id = +(
              userDataItem.traveler?.user_id ?? userDataItem.user_id
            )
            userDataItem.user_id = +(
              userDataItem.traveler?.user_id ?? userDataItem.user_id
            )
          } else {
            userDataItem = {
              included: null,
              owner: null,
              request_ticket_user_data_id: null,
              request_ticket_id: null,
              traveler: {
                gender: null,
                user_id: userDataItem.traveler?.user_id ?? userDataItem.user_id,
                name: null
              },
              user_id: userDataItem.traveler?.user_id ?? userDataItem.user_id
            }
          }
        })

        item.user_id = item.user_id
          ? +item.user_id
          : +item.request_ticket_user_data[0].traveler.user_id
      })

      draft.request_user_data = draft.request_user_data.map(item => ({
        cost_center: item.cost_center,
        included: item.included,
        reason_id: +item.reason_id,
        request_user_data_id: item.request_user_data_id
          ? +item.request_user_data_id
          : null,
        status: item.status,
        user_id: +item.traveler.user_id
      }))

      draft.request_custom_data.forEach(item => {
        item.contract_id = item.contract_id ? +item.contract_id : null
        item.target_id = item.target_id ? +item.target_id : null
      })
    })

    // ?without_layout=False
    const response: ApiResponse<ResponseDataFullRequest> = yield call(
      api.post,
      `/organizations/${organization.organization_id}/full_request${
        hasToodopayTickets ? '?toodopay=1' : ''
      }`,
      { full_request: encodedRequest }
    )

    if (response.ok) {
      let { request_number } = response.data.full_request
      if (request_number) {
        request_number = `#${request_number}`
      }

      const message = i18n.t(
        +request.request_id > 0
          ? 'common.push-notifications.request-changed-successful'
          : 'common.push-notifications.request-saved-successful',
        { value: request_number }
      )

      yield put(RequestsActions.saveFullRequestSuccess(message))
      yield put(resetAir())
      yield put(resetHotels())
      yield put(resetVehicles())
      if (setFullRequest) {
        yield put(RequestsActions.setFullRequest(response.data.full_request))
      }
      return
    }

    // @ts-ignore
    const reasons = response.data?.reasons

    if (typeof reasons === 'string') {
      const errorMessage = reasons.startsWith('Booking')
        ? i18n.t('common.response.booking-fail')
        : reasons

      yield put(RequestsActions.saveFullRequestFailure(errorMessage))
      return
    }

    // @ts-ignore
    const exception = response.data?.exception
    if (exception === 'TaxError') {
      yield put(
        RequestsActions.saveFullRequestFailure(
          i18n.t('common.response.air-tax-error')
        )
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.saveFullRequestFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga saveFullRequest', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.saveFullRequestFailure(message))
  }
}

export function* saveRequestTickets({
  payload: { tickets }
}: ReturnType<typeof RequestsActions.saveRequestTickets>) {
  try {
    const {
      me: {
        organization: { organization_id }
      },
      requests: {
        fullRequest: { request_id, request_tickets }
      }
    } = yield select((state: ApplicationState) => ({
      me: state.me,
      requests: state.requests
    }))

    const response: ApiResponse<ResponseDataTickets> = yield call(
      api.post,
      `/organizations/${organization_id}/requests/${request_id}/tickets`,
      { request_tickets: tickets }
    )

    if (response.ok) {
      const getTickets = response.data.request_tickets

      // Update local tickets

      /** list of ticket ids to remove, are the tickets that were sent and did not return */
      const ticketsToRemove = tickets
        .filter(
          ({ request_ticket_id }) =>
            ![
              ...getTickets.map(({ request_ticket_id }) => request_ticket_id),
              null
            ].includes(request_ticket_id)
        )
        .map(({ request_ticket_id }) => request_ticket_id) // [6,8] IDS

      /* list of tickets to overwrite, are the tickets that were sent and returned with change */
      const ticketsToChange = tickets.filter(({ request_ticket_id }) =>
        getTickets
          .map(({ request_ticket_id }) => request_ticket_id)
          .includes(request_ticket_id)
      )

      /* list of tickets to add, are tickets that were sent as null and returned with a new identifier */
      const ticketsToAdd = getTickets.filter(
        ({ request_ticket_id }) =>
          !tickets
            .map(({ request_ticket_id }) => request_ticket_id)
            .includes(request_ticket_id)
      )

      const finishTickets = [
        ...request_tickets
          .filter(
            ({ request_ticket_id }: RequestTicket) =>
              !ticketsToRemove.includes(request_ticket_id)
          )
          .map((ticket: RequestTicket) => {
            const found = ticketsToChange.find(
              ({ request_ticket_id }) =>
                request_ticket_id === ticket.request_ticket_id
            )

            return found || ticket
          }),
        ...ticketsToAdd
      ]

      yield put(RequestsActions.saveRequestTicketsSuccess(finishTickets))
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.saveRequestTicketsFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga saveRequestTickets', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.saveRequestTicketsFailure(message))
  }
}

export function* saveRequestItinerary({
  payload: { itinerary }
}: ReturnType<typeof RequestsActions.saveRequestItinerary>) {
  try {
    const {
      me: {
        organization: { organization_id }
      },
      requests: {
        fullRequest: { request_id }
      }
    } = yield select((state: ApplicationState) => ({
      me: state.me,
      requests: state.requests
    }))

    const response: ApiResponse<ResponseDataRequestItineraries> = yield call(
      api.post,
      `/organizations/${organization_id}/requests/${request_id}/request_itineraries`,
      { request_itineraries: [itinerary] }
    )
    if (response.ok) {
      // sort
      response.data.request_itineraries.sort((a, b) => {
        const started_on = a.started_on - b.started_on
        if (started_on === 0) {
          return a.request_itinerary_number < b.request_itinerary_number
            ? -1
            : 1
        }
        return started_on
      })
      yield put(
        RequestsActions.saveRequestItinerarySuccess(
          response.data.request_itineraries
        )
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.saveRequestItineraryFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga saveRequestItinerary', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.saveRequestItineraryFailure(message))
  }
}

export function* saveRequestStandardAssignments({
  payload: { request_standard_billings, request_standard_sharings }
}: ReturnType<typeof RequestsActions.saveRequestStandardAssignments>) {
  try {
    const {
      me: {
        organization: { organization_id }
      },
      requests: {
        fullRequest: { request_id }
      }
    } = yield select((state: ApplicationState) => ({
      me: state.me,
      requests: state.requests
    }))

    if (
      !request_standard_billings?.length &&
      !request_standard_sharings?.length
    ) {
      return
    }

    const request_standard_assignments: SaveRequestStandardAssignments = {}

    if (request_standard_billings) {
      request_standard_assignments.request_standard_billings =
        request_standard_billings
    }
    if (request_standard_sharings) {
      request_standard_assignments.request_standard_sharings =
        request_standard_sharings
    }

    const response: ApiResponse<ResponseDataRequestStandardAssignments> =
      yield call(
        api.post,
        `/organizations/${organization_id}/requests/${request_id}/standard_assignments`,
        { request_standard_assignments }
      )

    if (response.ok) {
      yield put(
        RequestsActions.saveRequestStandardAssignmentsSuccess(
          response.data.request_standard_assignments
        )
      )
      return
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.saveRequestStandardAssignmentsFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga saveRequestStandardAssignments', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.saveRequestStandardAssignmentsFailure(message))
  }
}

export function* setUnderwayStatus({
  payload: { request_id, status }
}: ReturnType<typeof RequestsActions.setUnderwayStatus>) {
  try {
    const {
      me: { user }
    } = yield select((state: ApplicationState) => state)

    const response: ApiResponse<ResponseDataRequests> = yield call(
      api.post,
      `users/${user.user_id}/requests/${request_id}/user_data`,
      {
        request_user_data: {
          status,
          user_id: user.user_id
        }
      }
    )

    if (response.ok) {
      yield put(RequestsActions.setUnderwayStatusSuccess())
      return
    }

    // @ts-ignore
    const { reasons } = response.data
    if (typeof reasons === 'string' && reasons) {
      yield put(RequestsActions.setUnderwayStatusFailure(reasons))
    }

    const message = findBasicApiResponseErrors(response)
    yield put(RequestsActions.setUnderwayStatusFailure(message))
  } catch (err) {
    ClientNotifyCatch('Saga setUnderwayStatus', err)
    const message = i18n.t('common.response.unexpected-error')
    yield put(RequestsActions.setUnderwayStatusFailure(message))
  }
}
